if (!customElements.get('journal-stories')) {
  customElements.define(
    'journal-stories',
    class JournalStories extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.list = this.querySelector('[data-journal-list]');
        this.button = this.querySelector('[data-journal-more]');
        this.status = this.querySelector('[data-journal-status]');
        if (!this.list || !this.button || !this.status) return;

        this.initialized = true;
        // Liquid owns translations; preserve the rendered label when loading finishes.
        this.moreLabel = this.button.textContent.trim();
        this.nextUrl = this.dataset.nextUrl;
        const cards = [...this.list.querySelectorAll('[data-article-id]')];
        this.seen = new Set([this.dataset.excludeId, ...cards.map((card) => card.dataset.articleId)]);
        // Shopify's archive has six rows per page; preserve extras between five-post clicks.
        this.pending = cards.slice(5);
        this.pending.forEach((card) => card.remove());
        this.button.hidden = !this.pending.length && !this.nextUrl;
        const pagination = this.closest('[data-journal-page]')?.querySelector('[data-journal-pagination]');
        if (pagination) pagination.hidden = true;
        this.button.addEventListener('click', () => this.loadMore());
      }

      disconnectedCallback() {
        this.controller?.abort();
      }

      async loadMore() {
        if (this.button.disabled) return;
        this.button.disabled = true;
        this.button.textContent = this.button.dataset.loading;
        this.status.hidden = true;
        this.list.setAttribute('aria-busy', 'true');
        this.controller = new AbortController();

        try {
          // A page can contain the current article or already-rendered rows. Keep
          // fetching until five new posts are available, or the archive ends.
          while (this.pending.length < 5 && this.nextUrl) {
            await this.fetchNextPage();
          }
          const added = this.pending.splice(0, 5);
          this.list.append(...added);
          this.button.hidden = !this.pending.length && !this.nextUrl;
          this.status.textContent = this.button.hidden
            ? this.button.dataset.allShown
            : this.button.dataset.loaded.replace('[count]', added.length);
          this.status.hidden = false;
          added[0]?.querySelector('.journal-story__title a')?.focus({ preventScroll: true });
        } catch (error) {
          if (error.name !== 'AbortError') {
            this.status.textContent = this.button.dataset.loadError;
            this.status.hidden = false;
          }
        } finally {
          this.button.disabled = false;
          this.button.textContent = this.moreLabel;
          this.list.removeAttribute('aria-busy');
        }
      }

      async fetchNextPage() {
        const url = new URL(this.nextUrl, window.location.href);
        if (url.origin !== window.location.origin) throw new Error('Invalid archive URL');
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error('Archive request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const page = html.querySelector('[data-journal-page]');
        if (!page) throw new Error('Archive content is missing');
        const nextUrl = page.dataset.nextUrl;
        if (nextUrl && new URL(nextUrl, url).href === url.href) throw new Error('Archive page did not advance');

        page.querySelectorAll('[data-article-id]').forEach((card) => {
          if (this.seen.has(card.dataset.articleId)) return;
          this.seen.add(card.dataset.articleId);
          this.pending.push(card);
        });
        // Advance only after a valid response, keeping failed requests retryable.
        this.nextUrl = nextUrl ? new URL(nextUrl, url).href : '';
      }
    }
  );
}
